//====== WR data 03/09/2026
// Motivo da alteração: porte do SmartFlow Importer do Portal LiveModa para a plataforma SmartFlow.
// Decisão do usuário: a configuração do Importer fica nos dados do PROJETO, editada no modal
// "Alterando Projeto". Este componente é a seção do modal — 2 blocos (Fornecedor / Cliente), cada
// um com: pasta do Google Drive (+ botão "Validar conexão", chama o backend), Grupo do Parceiro
// de Negócio (combobox alimentado pela Service Layer do projeto — GET /BusinessPartnerGroups, o
// login já foi feito pelo ProjectConsole ao abrir o console) e a sequência do CardCode
// (prefixo/contador).
//
// Controlado: recebe `value` (= project.smartflowImporter, { fornecedor:{}, cliente:{} }) e
// `onChange(next)` com o objeto inteiro.
//
// WR data 15/09/2026 — SmartFlow Item Importer (Fase 3): + seção "Itens (Materiais)", usando o
// MESMO value/onChange (agora com um 3º bloco, `item`, dentro do objeto). Só UM bloco pro lado de
// Item — não por papel: as duas pastas do Drive do projeto são lidas e deduplicadas JUNTAS.
//======
import React, { useEffect, useState } from "react"
import { Row, Col, Input, Button, Card, CardBody, CardHeader, Badge } from "reactstrap"

import { validateDriveFolder } from "services/smartflow/importerApi"
import { buscarGruposParceiroSap } from "services/smartflow/smartflowSapWrite"
import { buscarGruposItemSap, buscarUnidadesMedidaSap } from "services/smartflow/itemSapWrite"

const PAPEIS = [
  { tipo: "fornecedor", rotulo: "Fornecedores", prefixoPadrao: "F-" },
  { tipo: "cliente", rotulo: "Clientes", prefixoPadrao: "C-" },
]

export default function SmartFlowImporterConfig({ value, onChange, serviceLayerUrl }) {
  const cfg = value || {}
  const [aberto, setAberto] = useState(false)
  const [grupos, setGrupos] = useState([])
  const [gruposLoading, setGruposLoading] = useState(false)
  const [gruposErro, setGruposErro] = useState("")
  const [validando, setValidando] = useState({ fornecedor: false, cliente: false })

  //====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
  const [gruposItem, setGruposItem] = useState([])
  const [unidadesItem, setUnidadesItem] = useState([])
  const [gruposItemLoading, setGruposItemLoading] = useState(false)
  const [gruposItemErro, setGruposItemErro] = useState("")

  useEffect(() => {
    if (!aberto || !serviceLayerUrl) return
    let cancelado = false
    setGruposLoading(true)
    setGruposErro("")
    buscarGruposParceiroSap(undefined, serviceLayerUrl)
      .then((lista) => {
        if (!cancelado) setGrupos(lista)
      })
      .catch((error) => {
        if (!cancelado) {
          setGruposErro(
            "Não foi possível carregar os Grupos do SAP (" +
              (error?.message || "erro") +
              "). Abra o console do projeto primeiro para logar na Service Layer."
          )
        }
      })
      .finally(() => {
        if (!cancelado) setGruposLoading(false)
      })
    return () => {
      cancelado = true
    }
  }, [aberto, serviceLayerUrl])

  //====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
  useEffect(() => {
    if (!aberto || !serviceLayerUrl) return
    let cancelado = false
    setGruposItemLoading(true)
    setGruposItemErro("")
    Promise.all([buscarGruposItemSap(serviceLayerUrl), buscarUnidadesMedidaSap(serviceLayerUrl)])
      .then(([listaGrupos, listaUnidades]) => {
        if (cancelado) return
        setGruposItem(listaGrupos)
        setUnidadesItem(listaUnidades)
      })
      .catch((error) => {
        if (!cancelado) {
          setGruposItemErro(
            "Não foi possível carregar os Grupos de Item do SAP (" + (error?.message || "erro") + ")."
          )
        }
      })
      .finally(() => {
        if (!cancelado) setGruposItemLoading(false)
      })
    return () => {
      cancelado = true
    }
  }, [aberto, serviceLayerUrl])

  const patchPapel = (tipo, patch) => {
    onChange({
      ...cfg,
      [tipo]: { ...(cfg[tipo] || {}), ...patch },
    })
  }

  //====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
  const patchItem = (patch) => {
    onChange({
      ...cfg,
      item: { ...(cfg.item || {}), ...patch },
    })
  }

  const handleValidar = async (tipo) => {
    const papel = cfg[tipo] || {}
    const entrada = (papel.driveFolderInput || "").trim()
    if (!entrada) return
    setValidando((v) => ({ ...v, [tipo]: true }))
    try {
      const r = await validateDriveFolder(entrada)
      patchPapel(tipo, {
        driveFolderInput: entrada,
        driveFolderId: r.folderId,
        driveFolderName: r.folderName,
      })
    } catch (error) {
      patchPapel(tipo, { driveFolderId: null, driveFolderName: null })
      // eslint-disable-next-line no-alert
      alert(error?.message || "Falha ao validar a pasta do Drive.")
    } finally {
      setValidando((v) => ({ ...v, [tipo]: false }))
    }
  }

  return (
    <Card className="mt-3 mb-0">
      <CardHeader
        style={{ cursor: "pointer" }}
        onClick={() => setAberto((a) => !a)}
      >
        <h5 className="h4 mb-0">
          SmartFlow Importer {aberto ? "▾" : "▸"}
        </h5>
        <small className="text-muted">
          Pastas do Google Drive, Grupo do Parceiro de Negócio e sequência do CardCode (por papel) —
          e, abaixo, a config do modo Item (Materiais).
        </small>
      </CardHeader>

      {aberto && (
        <CardBody>
          {gruposErro && <div className="alert alert-warning py-2 small">{gruposErro}</div>}
          <Row>
            {PAPEIS.map(({ tipo, rotulo, prefixoPadrao }) => {
              const p = cfg[tipo] || {}
              const gruposDoPapel = grupos.filter((g) =>
                tipo === "cliente"
                  ? g.type === "bbpgt_CustomerGroup"
                  : g.type === "bbpgt_VendorGroup"
              )
              return (
                <Col md="6" key={tipo} className="mb-3">
                  <h5 className="text-uppercase text-muted mb-2">{rotulo}</h5>

                  <label className="form-control-label">Pasta do Google Drive (link ou ID)</label>
                  <Input
                    type="text"
                    bsSize="sm"
                    placeholder="https://drive.google.com/drive/folders/..."
                    value={p.driveFolderInput || ""}
                    onChange={(e) => patchPapel(tipo, { driveFolderInput: e.target.value })}
                  />
                  <div className="d-flex align-items-center mt-2" style={{ gap: "0.5rem" }}>
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => handleValidar(tipo)}
                      disabled={validando[tipo]}
                    >
                      {validando[tipo] ? "Validando..." : "Validar conexão"}
                    </Button>
                    {p.driveFolderId ? (
                      <Badge color="success">
                        Conectado: {p.driveFolderName || p.driveFolderId}
                      </Badge>
                    ) : (
                      <Badge color="secondary">Não validado</Badge>
                    )}
                  </div>

                  <label className="form-control-label mt-3">
                    Grupo do Parceiro de Negócio
                  </label>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={p.grupoCode != null ? String(p.grupoCode) : ""}
                    disabled={gruposLoading || !serviceLayerUrl}
                    onChange={(e) => {
                      const code = e.target.value ? Number(e.target.value) : null
                      const nome = gruposDoPapel.find((g) => String(g.code) === e.target.value)?.name || null
                      patchPapel(tipo, { grupoCode: code, grupoNome: nome })
                    }}
                  >
                    <option value="">
                      {gruposLoading ? "Carregando..." : "— selecione —"}
                    </option>
                    {gruposDoPapel.map((g) => (
                      <option key={g.code} value={String(g.code)}>
                        {g.name}
                      </option>
                    ))}
                    {p.grupoCode != null &&
                      !gruposDoPapel.some((g) => String(g.code) === String(p.grupoCode)) && (
                        <option value={String(p.grupoCode)}>
                          {p.grupoNome || `Grupo ${p.grupoCode}`}
                        </option>
                      )}
                  </Input>

                  <label className="form-control-label mt-3">Sequência do CardCode</label>
                  <div className="custom-control custom-checkbox mb-2">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`sf-seq-${tipo}`}
                      checked={p.pnUsaPrefixo ?? true}
                      onChange={(e) => patchPapel(tipo, { pnUsaPrefixo: e.target.checked })}
                    />
                    <label className="custom-control-label" htmlFor={`sf-seq-${tipo}`}>
                      Gerar CardCode automático (prefixo + número)
                    </label>
                  </div>
                  <Row>
                    <Col xs="4">
                      <label className="form-control-label small">Prefixo</label>
                      <Input
                        bsSize="sm"
                        placeholder={prefixoPadrao}
                        value={p.pnPrefixo || ""}
                        onChange={(e) => patchPapel(tipo, { pnPrefixo: e.target.value })}
                      />
                    </Col>
                    {/*====== WR data 03/09/2026 — "Próximo nº" e "Dígitos" eram type="number", que
                    descarta o zero à esquerda enquanto se digita (ex.: não dava pra digitar
                    "05030"). Viraram campo de texto que aceita só dígitos — o valor é guardado
                    como string e convertido pra número na hora de salvar (Mongoose casta
                    "05030" -> 5030; o zero à esquerda do CardCode vem do padStart pela qtd de
                    dígitos). ======*/}
                    <Col xs="4">
                      <label className="form-control-label small">Dígitos</label>
                      <Input
                        bsSize="sm"
                        type="text"
                        inputMode="numeric"
                        value={p.pnQtdCaracteresSequencia ?? ""}
                        onChange={(e) =>
                          patchPapel(tipo, {
                            pnQtdCaracteresSequencia: e.target.value.replace(/\D/g, ""),
                          })
                        }
                      />
                    </Col>
                    <Col xs="4">
                      <label className="form-control-label small">Próximo nº</label>
                      <Input
                        bsSize="sm"
                        type="text"
                        inputMode="numeric"
                        value={p.pnProximoNumero ?? ""}
                        onChange={(e) =>
                          patchPapel(tipo, { pnProximoNumero: e.target.value.replace(/\D/g, "") })
                        }
                      />
                    </Col>
                  </Row>
                </Col>
              )
            })}
          </Row>

          {/*====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
          Motivo: config do modo "Item" — só UM bloco (não fornecedor/cliente): as duas pastas do
          Drive do projeto são lidas e deduplicadas JUNTAS pro lado de Item (decisão do usuário). */}
          <hr />
          <h5 className="text-uppercase text-muted mb-2">Itens (Materiais)</h5>
          {gruposItemErro && <div className="alert alert-warning py-2 small">{gruposItemErro}</div>}
          {/*====== WR data 15/09/2026 — o toggle de IA fica numa linha própria, acima das duas
          colunas, pra "Grupo do Item" e "Sequência do ItemCode" começarem alinhados na mesma
          altura (antes o toggle só empurrava a coluna da esquerda pra baixo). */}
          <div className="custom-control custom-checkbox mb-3">
            <input
              type="checkbox"
              className="custom-control-input"
              id="sf-item-usar-ia"
              checked={cfg.item?.usarSugestaoIA ?? true}
              onChange={(e) => patchItem({ usarSugestaoIA: e.target.checked })}
            />
            <label className="custom-control-label" htmlFor="sf-item-usar-ia">
              Sugerir cadastro de Item com IA (Claude)
            </label>
          </div>
          <Row>
            <Col md="6">
              <label className="form-control-label">Grupo do Item (padrão)</label>
              {/*====== WR data 15/09/2026 — espaçador invisível do mesmo tamanho do checkbox
              "Gerar ItemCode automático" da coluna ao lado, só pra alinhar o início dos dois
              campos (a coluna da direita tem um checkbox entre o label e o input; esta não). */}
              <div className="custom-control custom-checkbox mb-2" style={{ visibility: "hidden" }} aria-hidden="true">
                <input type="checkbox" className="custom-control-input" id="sf-item-grupo-spacer" readOnly />
                <label className="custom-control-label" htmlFor="sf-item-grupo-spacer">
                  espaçador
                </label>
              </div>
              {/*====== WR data 15/09/2026 — 2º espaçador: a linha "Prefixo/Dígitos/Próximo nº" da
              coluna ao lado tem um label PRÓPRIO ("Prefixo") acima de cada caixa; o select de
              Grupo não tem label acima, então sem isso ele ficava uma linha mais alto do que
              devia (alinhado com o texto do checkbox, não com as caixas de input dele). */}
              <label className="form-control-label small" style={{ visibility: "hidden" }} aria-hidden="true">
                espaçador
              </label>
              <Input
                type="select"
                bsSize="sm"
                value={cfg.item?.grupoCode != null ? String(cfg.item.grupoCode) : ""}
                disabled={gruposItemLoading || !serviceLayerUrl}
                onChange={(e) => {
                  const code = e.target.value ? Number(e.target.value) : null
                  const nome = gruposItem.find((g) => String(g.code) === e.target.value)?.name || null
                  patchItem({ grupoCode: code, grupoNome: nome })
                }}
              >
                <option value="">{gruposItemLoading ? "Carregando..." : "— selecione —"}</option>
                {gruposItem.map((g) => (
                  <option key={g.code} value={String(g.code)}>
                    {g.name}
                  </option>
                ))}
                {cfg.item?.grupoCode != null &&
                  !gruposItem.some((g) => String(g.code) === String(cfg.item.grupoCode)) && (
                    <option value={String(cfg.item.grupoCode)}>
                      {cfg.item.grupoNome || `Grupo ${cfg.item.grupoCode}`}
                    </option>
                  )}
              </Input>
              {!unidadesItem.length && !gruposItemLoading && serviceLayerUrl && (
                <small className="text-muted">
                  Este tenant não tem Unidades de Medida cadastradas na Service Layer — o campo
                  Unidade fica como texto livre na tela do Importer.
                </small>
              )}
            </Col>
            <Col md="6">
              <label className="form-control-label">Sequência do ItemCode</label>
              <div className="custom-control custom-checkbox mb-2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="sf-item-seq"
                  checked={cfg.item?.itUsaPrefixo ?? true}
                  onChange={(e) => patchItem({ itUsaPrefixo: e.target.checked })}
                />
                <label className="custom-control-label" htmlFor="sf-item-seq">
                  Gerar ItemCode automático (prefixo + número)
                </label>
              </div>
              <Row>
                <Col xs="4">
                  <label className="form-control-label small">Prefixo</label>
                  <Input
                    bsSize="sm"
                    placeholder="IT-"
                    value={cfg.item?.itPrefixo || ""}
                    onChange={(e) => patchItem({ itPrefixo: e.target.value })}
                  />
                </Col>
                <Col xs="4">
                  <label className="form-control-label small">Dígitos</label>
                  <Input
                    bsSize="sm"
                    type="text"
                    inputMode="numeric"
                    value={cfg.item?.itQtdCaracteresSequencia ?? ""}
                    onChange={(e) =>
                      patchItem({ itQtdCaracteresSequencia: e.target.value.replace(/\D/g, "") })
                    }
                  />
                </Col>
                <Col xs="4">
                  <label className="form-control-label small">Próximo nº</label>
                  <Input
                    bsSize="sm"
                    type="text"
                    inputMode="numeric"
                    value={cfg.item?.itProximoNumero ?? ""}
                    onChange={(e) =>
                      patchItem({ itProximoNumero: e.target.value.replace(/\D/g, "") })
                    }
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      )}
    </Card>
  )
}
